import type { LoomVideo } from "~/services/ApiServiceContainer";

export interface HelpVideoOptions {
    watch?: boolean;
    debounce?: number;
    query?: MaybeRefOrGetter<string>;
    apps?: MaybeRefOrGetter<string[]>;
    canFetch?: () => boolean;
    onAfterFetch?: (data: LoomVideo[]) => LoomVideo[];
}

export interface HelpOptions {
    watch?: boolean;
    debounce?: number;
    query?: MaybeRefOrGetter<string>;
    apps?: MaybeRefOrGetter<string[]>;
    canFetch?: () => boolean;
    onAfterVideoFetch?: (data: LoomVideo[]) => LoomVideo[];
    onAfterArticleFetch?: (data: any[]) => any[];
}

export interface HelpArticleOptions {
    watch?: boolean;
    debounce?: number;
    query?: MaybeRefOrGetter<string>;
    app?: MaybeRefOrGetter<string>;
    canFetch?: () => boolean;
    onAfterFetch?: (data: any[]) => any[];
}

export function useHelpVideos(options: HelpVideoOptions = {}) {
    const query = isRef(options.query) ? options.query : ref<string>((options.query ?? "") as string);
    const apps = isRef(options.apps) ? options.apps : ref<string[]>((options.apps ?? []) as string[]);

    const api = useApi();

    const { data, status, refresh, error, clear } = useAsyncData(async () => {
        try {
            if (options.canFetch && !options.canFetch()) {
                return [];
            }

            const data = await api.searchLoomVideos(query.value, apps.value);

            return data.data;
        } catch {
            return [];
        }
    });

    if (options.watch ?? true) {
        watchDebounced(query, async () => {
            await refresh();
        }, {
            debounce: options.debounce ?? 250
        });
    }

    return {
        data: computed(() => {
            if (options.onAfterFetch) {
                return options.onAfterFetch(data.value ?? []);
            }

            return data.value ?? [];
        }), status, refresh, error, clear
    };
}

export function useHelpArticles(options: HelpArticleOptions = {}) {
    const query = isRef(options.query) ? options.query : ref<string>((options.query ?? "") as string);
    const app = isRef(options.app) ? options.app : ref<string>((options.app ?? []) as string);

    const { data, status, refresh, error, clear } = useAsyncData(async () => {
        try {
            if (options.canFetch && !options.canFetch()) {
                return [];
            }

            const { data } = await useFetch("/api/help", {
                query: {
                    query: `${app.value} ${query.value}`.trim()
                }
            });

            return data.value?.data?.articles ?? [] as unknown as any[];
        } catch {
            return [];
        }
    });

    if (options.watch ?? true) {
        watchDebounced(query, async () => {
            await refresh();
        }, {
            debounce: options.debounce ?? 250
        });
    }

    return {
        data: computed(() => {
            if (options.onAfterFetch) {
                return options.onAfterFetch(data.value ?? []);
            }

            return data.value ?? [];
        }), status, refresh, error, clear
    };
}

export function useHelpArticlesAndVideos(options: HelpOptions) {
    const query = isRef(options.query) ? options.query : ref<string>((options.query ?? "") as string);

    const app = computed(() => {
        let apps: string[];
        if (isRef(options.apps)) {
            apps = options.apps.value;
        } else {
            apps = options.apps as string[];
        }

        return apps.length ? apps[0] : "";
    });

    const videos = useHelpVideos({
        query: options.query ?? "",
        apps: options.apps ?? [],
        canFetch: options.canFetch,
        onAfterFetch: options.onAfterVideoFetch,
        watch: false
    });
    const articles = useHelpArticles({
        query: options.query ?? "",
        app: app,
        canFetch: options.canFetch,
        onAfterFetch: options.onAfterArticleFetch,
        watch: false
    });

    function refresh() {
        return Promise.allSettled([
            videos.refresh(),
            articles.refresh()
        ]);
    }

    function clear() {
        videos.clear();
        articles.clear();
    }

    if (options.watch ?? true) {
        watchDebounced(query, async () => {
            await refresh();
        }, {
            debounce: options.debounce ?? 250
        });
    }

    const status = computed(() => {
        const statuses = [videos.status.value, articles.status.value];

        if (statuses.includes("pending")) {
            return "pending";
        } else if (statuses.includes("error")) {
            return "error";
        } else if (statuses.includes("idle")) {
            return "idle";
        }

        return "success";
    });

    const data = computed(() => ({
        articles: (articles.data.value ?? []).map((article: any) => {
            const video = (videos.data.value ?? []).find(video => article.body.includes(`/embed/${video.id}`))

            return {
                ...article,
                video: video
            };
        }),
        videos: (videos.data.value ?? []).map(video => {
            const article = (articles.data.value ?? []).find((article: any) => article.body.includes(`/embed/${video.id}`))
            return {
                ...video,
                article: article
            };
        }),
    }));

    const error = computed(() => ({
        articles: articles.error.value,
        videos: videos.error.value
    }));

    return {
        data,
        status,
        refresh,
        clear,
        error
    };
}

export function useCoreAppArticlesFilter(articles: any[], app: string, core_apps: MaybeRefOrGetter<string[]> = []): any[] {
    core_apps = isRef(core_apps) ? core_apps : ref<string[]>(core_apps as string[]);

    return articles.filter(article => {
        for (const app of core_apps.value) {
            if (article.title.toLowerCase().includes(app.toLowerCase())) {
                return false;
            }
        }

        return article.title.toLowerCase().includes((app).toLowerCase());
    }) as any[];
}